import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../../components/Header.jsx";
import { API_URL } from "../../config";

const API = API_URL;

const getStatusColor = (statut = "") => {
  switch (statut) {
    case "Disponible": return "#096943";
    case "Indisponible":
    case "Aux Ateliers": return "#ff1900ff";
    case "Affecté": return "#0080f8ff";
    case "Au CT": return "#ff9100ff";
    case "Réformé":
    case "A venir":
    case "A VENIR": return "#000000ff";
    default: return "#7f8c8d";
  }
};

const Badge = ({ children, color }) => (
  <span style={{
    display: "inline-block", padding: "6px 12px", borderRadius: 999,
    background: color, color: "#fff", fontWeight: 700, fontSize: 13
  }}>{children}</span>
);

// ——— Carrousel réduit (à droite)
const Carousel = ({ images = [], alt = "" }) => {
  const [idx, setIdx] = useState(0);
  if (!images.length) return null;
  return (
    <div style={{
      position: "relative",
      maxWidth: 520, width: "100%", aspectRatio: "4/3",
      marginLeft: "auto", overflow: "hidden", borderRadius: 12,
      boxShadow: "0 6px 16px rgba(0,0,0,.12)", background: "#eee",
    }}>
      <img src={images[idx]} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <button aria-label="Précédent" onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)} style={arrowStyle("left")}>‹</button>
      <button aria-label="Suivant" onClick={() => setIdx((i) => (i + 1) % images.length)} style={arrowStyle("right")}>›</button>
      <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6 }}>
        {images.map((_, i) => (
          <span key={i} onClick={() => setIdx(i)} style={{
            width: 8, height: 8, borderRadius: 10, cursor: "pointer",
            background: i === idx ? "white" : "rgba(255,255,255,.6)",
            outline: i === idx ? "2px solid rgba(0,0,0,.15)" : "none",
          }}/>
        ))}
      </div>
    </div>
  );
};
const arrowStyle = (side) => ({
  position: "absolute", top: "50%", transform: "translateY(-50%)",
  [side]: 8, width: 34, height: 34, borderRadius: 999, border: "none",
  background: "rgba(0,0,0,.45)", color: "#fff", fontSize: 20, fontWeight: 700,
  lineHeight: "34px", cursor: "pointer", display: "grid", placeItems: "center",
});

// ——— Barre remplie (couleur paramétrable)
const Bar = ({ value = 0, color = "#27ae60" }) => (
  <div>
    <div style={{ height: 10, background: "#eef2f7", borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
      <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, height: "100%", background: color }} />
    </div>
    <div style={{ textAlign: "right", opacity: .8 }}>{value}%</div>
  </div>
);

const Vehicule = () => {
  const { parc } = useParams();
  const navigate = useNavigate();

  const [vehicule, setVehicule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // modal état
  const [showEtat, setShowEtat] = useState(false);
  const [nouvelEtat, setNouvelEtat] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/vehicles/${parc}`);
        if (!r.ok) throw new Error("Véhicule introuvable");
        const data = await r.json();
        if (mounted) setVehicule(data);
      } catch (e) {
        setErr(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [parc]);

  const images = useMemo(() => {
    let arr = [];
    try { arr = vehicule?.photosJson ? JSON.parse(vehicule.photosJson) : []; } catch {}
    if (!arr?.length) {
      arr = [
        "/images/vehicule_avant.jpg",
        "/images/vehicule_interieur.jpg",
        "/images/vehicule_profil_gauche.jpg",
        "/images/vehicule_profil_droit.jpg",
        "/images/vehicule_arriere.jpg",
      ];
    }
    return arr;
  }, [vehicule]);

  const updateField = async (patch) => {
    const r = await fetch(`${API}/api/vehicles/${parc}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) throw new Error(await r.text());
    setVehicule(await r.json());
  };

  const handleChangeEtat = async () => {
    if (!nouvelEtat) return;
    try {
      await updateField({ statut: nouvelEtat });
      setShowEtat(false);
      setNouvelEtat("");
    } catch (e) {
      alert("Erreur: " + e.message);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Supprimer définitivement le véhicule ${parc} ? Avez-vous préparé la sortie de parc ?`)) return;
    try {
      const r = await fetch(`${API}/api/vehicles/${parc}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await r.text());
      navigate("/abribus/vehicules");
    } catch (e) {
      alert("Erreur: " + e.message);
    }
  };

  if (loading) return (<><Header /><main style={pageStyle}><p>Chargement…</p></main></>);
  if (err || !vehicule) {
    return (
      <>
        <Header />
        <main style={pageStyle}>
          <h1 style={titleStyle}>Véhicule introuvable</h1>
          <p>Le numéro de parc <strong>{parc}</strong> ne correspond à aucun véhicule connu.</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main style={pageStyle}>
        {/* Titre gras */}
        <h1 style={titleStyle}>VÉHICULE N°{vehicule.parc} – {vehicule.modele}</h1>

        {/* grille : infos à gauche / carrousel réduit à droite */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 520px",
          gap: 28,
          alignItems: "start",
          width: "100%",
          maxWidth: 1100,
        }}>
          {/* Infos de base (gauche) */}
          <section>
            <h2 style={h2Style}>Informations de base</h2>
            <InfoRow label="Numéro de Parc" value={vehicule.parc} />
            <InfoRow label="Type" value={vehicule.type} />
            <InfoRow label="Modèle" value={vehicule.modele} />
            {vehicule.marque && <InfoRow label="Marque" value={vehicule.marque} />}
            <InfoRow label="Immatriculation" value={vehicule.immat} badge />
            <InfoRow label="Moteur" value={vehicule.moteur || "-"} />
            {vehicule.boite && <InfoRow label="Boîte" value={vehicule.boite} />}
            {vehicule.annee && <InfoRow label="Année" value={vehicule.annee} />}
            <InfoRow label="Mise en Service" value={vehicule.miseEnService ? new Date(vehicule.miseEnService).toLocaleDateString() : "-"} />
            <InfoRow label="Km" value={`${Number(vehicule.km || 0).toLocaleString()} km`} highlight />
            <InfoRow label="Prochain CT" value={vehicule.ct ? new Date(vehicule.ct).toLocaleDateString() : "-"} chip />
            {vehicule.pmr && <InfoRow label="PMR" value="✓ Équipé" chip />}
          </section>

          {/* Carrousel + statut (droite) */}
          <aside>
            <Carousel images={images} alt={`Véhicule ${vehicule.parc}`} />
            <div style={{ marginTop: 10 }}>
              <Badge color={getStatusColor(vehicule.statut)}>{vehicule.statut}</Badge>
            </div>
            {vehicule.etablissementId && (
              <div style={{ marginTop: 16, padding: 12, background: '#f0f8ff', borderRadius: 8, borderLeft: '4px solid #0080f8' }}>
                <p style={{ margin: 0, fontSize: 12, color: '#666', fontWeight: 600 }}>ÉTABLISSEMENT</p>
                <Link to={`/abribus/etablissement/${vehicule.etablissementId}`} style={{ margin: '6px 0 0 0', fontSize: 14, fontWeight: 600, color: '#0080f8', textDecoration: 'none', display: 'inline-block' }}>
                  → {vehicule.etablissement?.nom || 'Établissement'}
                </Link>
              </div>
            )}
          </aside>
        </div>

        {/* ——— BLOC ACTIONS : Éditer / Changer état / Supprimer ——— */}
        <div style={{ width: "100%", maxWidth: 1100, display: "flex", gap: 12, flexWrap: "wrap", marginTop: 24, justifyContent: "space-between" }}>
          <button className="btn primary" onClick={() => navigate(`/abribus/vehicule/${parc}/edit`)} style={{ background: '#2980b9', color: '#fff', fontWeight: 600, borderRadius: 6, padding: '8px 22px', fontSize: '1rem', border: 'none', cursor: 'pointer' }}>
            Éditer le véhicule
          </button>
          <button
            className="btn"
            style={{ background: '#f1c40f', color: '#222', fontWeight: 600, borderRadius: 6, padding: '8px 22px', fontSize: '1rem', border: 'none', cursor: 'pointer' }}
            onClick={() => setShowEtat(true)}
            title="Changer l'état du véhicule"
          >
            Changer état
          </button>
          <button
            className="btn danger"
            style={{ background: '#e74c3c', color: '#fff', fontWeight: 600, borderRadius: 6, padding: '8px 22px', fontSize: '1rem', border: 'none', cursor: 'pointer' }}
            onClick={handleDelete}
          >
            Supprimer
          </button>
        </div>
        {/* ——— Jauges (remplies) ——— */}
        <hr style={{ margin: "18px 0" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, width: "100%", maxWidth: 1100 }}>
          <Card title="Propreté"><Bar value={vehicule.proprete ?? 100} color="#096943" /></Card>
          <Card title="État technique"><Bar value={vehicule.etatTechnique ?? 100} color="#096943" /></Card>
          <Card title="État intérieur"><Bar value={vehicule.etatInterieur ?? 100} color="#096943" /></Card>
        </div>

        {/* ——— Options ——— */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, width: "100%", maxWidth: 1100, marginTop: 18 }}>
          <Card title="Options d'usine"><SmallList jsonText={vehicule.optionsUsineJson} /></Card>
          <Card title="Options d'atelier"><SmallList jsonText={vehicule.optionsAtelierJson} /></Card>
          <Card title="Options SAEIV"><SmallList jsonText={vehicule.optionsSaeivJson} /></Card>
        </div>
        {/* ——— Modal Changer l'état ——— */}
        {showEtat && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowEtat(false)}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 32, minWidth: 360, maxWidth: 480, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }} onClick={e => e.stopPropagation()}>
              <h2 style={{ marginTop: 0, marginBottom: 20, color: '#2c3e50', fontSize: '1.3rem', fontWeight: 700 }}>Changer l'état du véhicule</h2>
              <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: 16 }}>État actuel: <strong>{vehicule.statut}</strong></p>
              <select value={nouvelEtat} onChange={e => setNouvelEtat(e.target.value)} style={{ width: "100%", padding: 12, borderRadius: 8, marginBottom: 24, fontSize: '0.95rem', fontWeight: 600, border: '1px solid #ddd' }}>
                <option value="">-- Sélectionner un état --</option>
                <option value="Disponible">Disponible</option>
                <option value="Indisponible">Indisponible</option>
                <option value="Aux Ateliers">Aux Ateliers</option>
                <option value="Affecté">Affecté</option>
                <option value="Au CT">Au CT</option>
                <option value="Réformé">Réformé</option>
                <option value="A VENIR">A VENIR</option>
              </select>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button style={{ background: '#eee', color: '#222', borderRadius: 6, padding: '10px 20px', fontWeight: 600, fontSize: '0.95rem', border: 'none', cursor: 'pointer' }} onClick={() => setShowEtat(false)}>Annuler</button>
                <button style={{ background: '#2980b9', color: '#fff', borderRadius: 6, padding: '10px 20px', fontWeight: 700, fontSize: '0.95rem', border: 'none', cursor: 'pointer' }} onClick={handleChangeEtat} disabled={!nouvelEtat}>Valider</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

/* ——— helpers UI ——— */

const pageStyle = {
  padding: "24px",
  fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto",
  backgroundColor: "#f0f4f8",
  minHeight: "calc(100vh - 160px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};
const titleStyle = {
  fontWeight: 800, fontSize: "1.8rem", color: "#2c3e50",
  textAlign: "left", width: "100%", maxWidth: 1100, margin: "4px auto 20px",
};
const h2Style = { margin: "0 0 10px", fontSize: "1.1rem", color: "#2c3e50" };

const InfoRow = ({ label, value, badge, highlight, chip }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0" }}>
    <span style={{ minWidth: 160, color: "#6b7785" }}><strong>{label} :</strong></span>
    {badge ? (
      <span style={{ border: "2px solid #111", borderRadius: 6, padding: "2px 8px", fontWeight: 700 }}>{value}</span>
    ) : chip ? (
      <span style={{ background: "#27ae60", color: "#fff", padding: "4px 8px", borderRadius: 8, fontWeight: 700 }}>
        {value}
      </span>
    ) : (
      <span style={{
        fontWeight: 600, color: highlight ? "#1a1a1a" : "#2c3e50",
        background: highlight ? "#FEF3C7" : "transparent",
        padding: highlight ? "2px 6px" : 0, borderRadius: 6
      }}>{value}</span>
    )}
  </div>
);

const Card = ({ title, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 8px 20px rgba(0,0,0,.06)" }}>
    <h3 style={{ margin: "0 0 10px", color: "#2c3e50" }}>{title}</h3>
    {children}
  </div>
);

const SmallList = ({ jsonText }) => {
  let data = null;
  try { data = jsonText ? JSON.parse(jsonText) : null; } catch {}
  if (!data) return <p style={{ opacity: .6 }}>Aucune donnée.</p>;
  if (Array.isArray(data)) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {data.map((x, i) => (
          <span key={i} style={{
            background: "#6c3483", color: "#fff", padding: "4px 8px",
            borderRadius: 20, fontWeight: 700, fontSize: 12
          }}>{String(x)}</span>
        ))}
      </div>
    );
  }
  const entries = Object.entries(data);
  if (!entries.length) return <p style={{ opacity: .6 }}>Aucune option.</p>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {entries.map(([k, v]) => (
        <li key={k} style={{ margin: "4px 0" }}>
          {k} {v === true ? "✓" : v === false ? "✗" : `: ${String(v)}`}
        </li>
      ))}
    </ul>
  );
};

export default Vehicule;
